import { useState } from 'react';
import { withTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';
import { env } from 'env';
import { format } from 'date-fns';
import ButtonTooltip from '@core/dashboard/components/ButtonTooltip';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  ClickAwayListener,
  Typography,
} from '@mui/material';
import { useStyles } from './styles';
import { convertUnixTimeToMilliSeconds, ellipsizeText } from '@helpers/events';
import { ISelectEventItemViewProps } from './interfaces';
import { CalendarIcon } from './CalendarIcon';
import { EventStatus } from '@interfaces/event';

const { REACT_APP_MAX_CHARS_EVENT_NAME } = env;

const SelectEventItemView = ({
  event,
  isSelectedEvent,
  setSelectedEvent,
  showDateBar,
  tooltipText,
  eventTooptipNameOpen,
  handleEventNameTooltipOpen,
  eventTooptipOpen,
  handleEventTooltipOpen,
  handleTooltipClose,
}: ISelectEventItemViewProps) => {
  const [imgError, setImgError] = useState(false);

  const classes = useStyles(isSelectedEvent);
  const MAX_CHARS_EVENT_NAME = Number(REACT_APP_MAX_CHARS_EVENT_NAME) || 45;
  const eventDate = new Date(
    convertUnixTimeToMilliSeconds(
      (event.inviteStartTime || 0) > 0
        ? event.inviteStartTime || 0
        : event.startTime,
    ),
  );

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Box sx={classes.root}>
        <Card sx={classes.card}>
          <Box
            sx={
              event.isDisabled
                ? {
                    ...classes.eventPicContainer,
                    ...classes.eventPicContainerDisabled,
                  }
                : classes.eventPicContainer
            }
            onClick={() => {
              if (!event.isDisabled && event.status === EventStatus.ACTIVE) {
                setSelectedEvent(event);
              }
              handleEventTooltipOpen();
            }}
          >
            {event.eventRelease?.clientVersion ? (
              <Chip
                label={`Ver: ${event.eventRelease.clientVersion}`}
                size="small"
                sx={classes.chipVersion}
              />
            ) : null}
            {showDateBar && !event.isDisabled && (
              <Box sx={classes.dateTimeOverlay}>
                <CalendarIcon />
                <Typography sx={classes.dateTimeText}>
                  {format(eventDate, 'd LLL yyyy k:mm z')}
                </Typography>
              </Box>
            )}
            <ButtonTooltip
              title={tooltipText}
              sx={{
                tooltip: {
                  padding: '4px 16px',
                  textAlign: 'center',
                },
              }}
              disableHover={!event.isDisabled}
              open={eventTooptipOpen}
              manualClick={isMobileOnly && event.isDisabled}
            >
              {!event.logoImage?.url || imgError ? (
                <Box
                  sx={{
                    ...classes.eventPic,
                    backgroundColor: '#F0F0F0',
                  }}
                />
              ) : (
                <CardMedia
                  component="img"
                  image={event.logoImage ? event.logoImage.url : ''}
                  alt="Event Miniature"
                  sx={classes.eventPic}
                  onError={() => setImgError(true)}
                />
              )}
            </ButtonTooltip>
          </Box>
          <CardContent sx={classes.text} onClick={handleEventNameTooltipOpen}>
            <ButtonTooltip
              title={event.name}
              sx={{
                tooltip: {
                  padding: '4px 16px',
                },
              }}
              disableHover={event.name.length < MAX_CHARS_EVENT_NAME}
              open={eventTooptipNameOpen}
              manualClick={isMobileOnly}
            >
              <Box>{ellipsizeText(event.name, MAX_CHARS_EVENT_NAME)}</Box>
            </ButtonTooltip>
          </CardContent>
        </Card>
      </Box>
    </ClickAwayListener>
  );
};

export default withTranslation()(SelectEventItemView);
